import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListPensionBenefitPayoutCoefficientRes } from 'app/interfaces/responses/Exposed/exposedListPensionBenefitPayoutCoefficientRes';
import { PensionService } from 'app/services/api/pension/pension.service';
import { PENSIONACQUISITIONTYPE } from 'common/constants/pensionAcquisitionType';

@Component({
    selector: 'pension-coefficients-payout',
    templateUrl: './pension-coefficients-payout.component.html',
})
export class PensionCoefficientsPayoutComponent implements OnInit {
    @ViewChild(MatSort) sort: MatSort;
    protected response: ExposedListPensionBenefitPayoutCoefficientRes[];
    protected dataSource: MatTableDataSource<any>;
    protected tableColumns: string[] = ['age', 'monthlyCoefficient', 'accumulatedCoefficient', 'payoutMultiplier'];

    private acquisitionChangeDate: Date = new Date(2023, 0, 1);
    protected equalCoefficientsPreAcquisitionChangeDS: MatTableDataSource<ExposedListPensionBenefitPayoutCoefficientRes>;
    protected equalCoefficientsPostAcquisitionChangeOldBenefitsDS: MatTableDataSource<ExposedListPensionBenefitPayoutCoefficientRes>;
    protected equalCoefficientsPostAcquisitionChangeNewBenefitsDS: MatTableDataSource<ExposedListPensionBenefitPayoutCoefficientRes>;
    protected ageRelatedDS: MatTableDataSource<ExposedListPensionBenefitPayoutCoefficientRes>;

    protected displayedDataSource: MatTableDataSource<ExposedListPensionBenefitPayoutCoefficientRes>;

    protected acquisitionTypeOptions = [PENSIONACQUISITIONTYPE.AGERELATEDACQUISITION, PENSIONACQUISITIONTYPE.EQUALACQUISITION];
    protected periodOptions = [
        {
            value: '<2022',
            viewValue: 'Fyrir réttindabreytingar',
        } as DropDownItem,
        {
            value: '>2023',
            viewValue: 'Eftir réttindabreytingar',
        } as DropDownItem,
    ];
    protected zeroPointOptions = [
        { value: 65, viewValue: '0 punktur 65' } as DropDownItem,
        { value: 67, viewValue: '0 punktur 67' } as DropDownItem,
    ];

    protected selectedAcquisitionType = this.acquisitionTypeOptions[0];
    protected selectedPeriod = this.periodOptions[0].value;
    protected selectedZeroPoint = this.zeroPointOptions[0].value;

    constructor(
        private pensionService: PensionService,
        public dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.refresh();
    }

    refresh(): void {
        this.pensionService.getPensionBenefitPayoutCoefficients().then((res) => {
            this.response = res;

            this.ageRelatedDS = new MatTableDataSource(
                this.response
                    .filter((item) => item.acquisitionType == PENSIONACQUISITIONTYPE.AGERELATEDACQUISITION)
                    .sort((a, b) => a.age - b.age)
            );
            this.calculateAccumulatedCoefficients(this.ageRelatedDS);

            this.equalCoefficientsPreAcquisitionChangeDS = new MatTableDataSource(
                this.response.filter(
                    (item) =>
                        item.acquisitionType == PENSIONACQUISITIONTYPE.EQUALACQUISITION &&
                        item.acquisitionPeriodTo != null &&
                        new Date(item.acquisitionPeriodTo) < this.acquisitionChangeDate &&
                        item.validTo != null &&
                        new Date(item.validTo) < this.acquisitionChangeDate &&
                        item.validFrom == null &&
                        item.acquisitionPeriodFrom == null
                )
            );
            this.calculateAccumulatedCoefficients(this.equalCoefficientsPreAcquisitionChangeDS);

            this.equalCoefficientsPostAcquisitionChangeOldBenefitsDS = new MatTableDataSource(
                this.response.filter(
                    (item) =>
                        item.acquisitionType == PENSIONACQUISITIONTYPE.EQUALACQUISITION &&
                        item.acquisitionPeriodTo != null &&
                        new Date(item.acquisitionPeriodTo) < this.acquisitionChangeDate &&
                        item.validFrom != null &&
                        new Date(item.validFrom).getTime() == this.acquisitionChangeDate.getTime() &&
                        item.validTo == null &&
                        item.acquisitionPeriodFrom == null
                )
            );
            this.calculateAccumulatedCoefficients(this.equalCoefficientsPostAcquisitionChangeOldBenefitsDS);

            this.equalCoefficientsPostAcquisitionChangeNewBenefitsDS = new MatTableDataSource(
                this.response.filter(
                    (item) =>
                        item.acquisitionType == PENSIONACQUISITIONTYPE.EQUALACQUISITION &&
                        item.acquisitionPeriodFrom != null &&
                        new Date(item.acquisitionPeriodFrom).getTime() == this.acquisitionChangeDate.getTime() &&
                        item.validFrom != null &&
                        new Date(item.validFrom).getTime() == this.acquisitionChangeDate.getTime() &&
                        item.validTo == null &&
                        item.acquisitionPeriodTo == null
                )
            );
            this.calculateAccumulatedCoefficients(this.equalCoefficientsPostAcquisitionChangeNewBenefitsDS);

            // Set the initial displayed datasource to the age related data source
            this.displayedDataSource = this.ageRelatedDS;
        });
    }

    calculateAccumulatedCoefficients = (dataSource: MatTableDataSource<ExposedListPensionBenefitPayoutCoefficientRes>): void => {
        let zeroPoint = dataSource.data.findIndex((item) => item.monthlyCoefficient == 0);
        if (zeroPoint < 0) {
            return;
        }
        // Set the zero point item accumulated coefficient
        dataSource.data[zeroPoint].accumulatedCoefficient = dataSource.data[zeroPoint].monthlyCoefficient;

        // Calculate accumulated coefficients for rushed payout
        for (let i = zeroPoint - 1; i >= 0; i--) {
            dataSource.data[i].accumulatedCoefficient =
                dataSource.data[i].monthlyCoefficient * 12 + dataSource.data[i + 1].accumulatedCoefficient;
        }

        // Calculate accumulated coefficients for deulayed payout
        for (let i = zeroPoint + 1; i < dataSource.data.length; i++) {
            dataSource.data[i].accumulatedCoefficient =
                dataSource.data[i].monthlyCoefficient * 12 + dataSource.data[i - 1].accumulatedCoefficient;
        }
    };

    onOptionsChange = () => {
        if (this.selectedAcquisitionType == PENSIONACQUISITIONTYPE.AGERELATEDACQUISITION) {
            this.displayedDataSource = this.ageRelatedDS;
        } else if (this.selectedPeriod == '<2022') {
            this.displayedDataSource = this.equalCoefficientsPreAcquisitionChangeDS;
        } else if (this.selectedZeroPoint == 65) {
            this.displayedDataSource = this.equalCoefficientsPostAcquisitionChangeOldBenefitsDS;
        } else {
            this.displayedDataSource = this.equalCoefficientsPostAcquisitionChangeNewBenefitsDS;
        }
    };
}
